<template>
    <div v-if="!pedidos || pedidos.length === 0" class="alinha-centro">
        <span class="chip bg-cinza">Nenhum pedido de mudança solicitado</span>
    </div>
    <div v-else>
        <div v-for="(p, index) in pedidos" :key="p.id ?? index" class="pedido-mudanca-item alinha-v">
            <h3 class="alinha-centro pedido-mudanca-ordem">{{ `${pedidos.length - index}º` }}</h3>
            <fieldset class="grid-4 bloco2 margem pedido-mudanca-fieldset">
                <div>
                    <label>Campo</label>
                    <input type="text" readonly :value="labelCampo(p)">
                </div>
                <div>
                    <label>Valor Original</label>
                    <textarea v-if="usaTextarea(p)" :value="formatarValorExibicao(p, 'atual')"
                        readonly></textarea>
                    <input v-else-if="metaCampo(p.campo)?.tipo === 'date'" type="date"
                        :value="dataParaInput(p.valor_atual)" readonly />
                    <select v-else-if="metaCampo(p.campo)?.tipo === 'select'" :value="p.valor_atual" disabled
                        class="select-readonly">
                        <option :value="null" hidden>-</option>
                        <option v-for="(o, idx) in metaCampo(p.campo)?.lista" :key="idx" :value="o">{{ o }}</option>
                    </select>
                    <input v-else-if="metaCampo(p.campo)?.tipo === 'moeda'" type="text"
                        :value="formatarMoeda(p.valor_atual)" readonly />
                    <input v-else type="text" :value="formatarValorExibicao(p, 'atual')" readonly />
                </div>
                <div>
                    <label>Novo Valor</label>
                    <textarea v-if="usaTextarea(p)" :value="formatarValorExibicao(p, 'novo')"
                        readonly></textarea>
                    <input v-else-if="metaCampo(p.campo)?.tipo === 'date'" type="date"
                        :value="dataParaInput(p.novo_valor)" readonly />
                    <select v-else-if="metaCampo(p.campo)?.tipo === 'select'" :value="p.novo_valor" disabled
                        class="select-readonly">
                        <option :value="null" hidden>-</option>
                        <option v-for="(o, idx) in metaCampo(p.campo)?.lista" :key="idx" :value="o">{{ o }}</option>
                    </select>
                    <input v-else-if="metaCampo(p.campo)?.tipo === 'moeda'" type="text"
                        :value="formatarMoeda(p.novo_valor)" readonly />
                    <input v-else type="text" :value="formatarValorExibicao(p, 'novo')" readonly />
                </div>
                <div>
                    <label>Solicitado Por</label>
                    <input type="text" readonly :value="p.solicitante?.name || '—'">
                </div>
                <div>
                    <label>Data da Solicitação</label>
                    <input type="date" :value="dataParaInput(p.created_at)" readonly>
                </div>
                <div class="col-2">
                    <label>Justificativa</label>
                    <textarea :value="p.justificativa" readonly></textarea>
                </div>
                <div v-if="p.obs">
                    <label>Observações</label>
                    <textarea :value="p.obs" readonly></textarea>
                </div>
                <div class="pedido-mudanca-status">
                    <label>Status</label>
                    <span v-if="p.status === 'pendente'" class="chip bg-cinza">Pendente</span>
                    <span v-else class="chip" :class="p.status === 'aprovado' ? 'bg-sucesso' : 'bg-erro'">
                        {{ p.status === 'aprovado' ? 'Aprovado' : 'Reprovado' }}
                        <template v-if="p.aprovador?.name"> por {{ p.aprovador.name }}</template>
                    </span>
                </div>
            </fieldset>
        </div>
    </div>
</template>

<script>
import servicePCM from '@/services/servicePCM'
import { dataParaInput, formatarDataBr } from '@/utils/date'

export default {
    name: 'PedidosMudancaLista',

    props: {
        pedidos: {
            type: Array,
            default: () => [],
        },
        campos: {
            type: Object,
            default: () => servicePCM.campos,
        },
    },

    methods: {
        dataParaInput,
        labelCampo(pedidoOuCampo) {
            if (pedidoOuCampo && typeof pedidoOuCampo === 'object') {
                return servicePCM.labelCampoPedidoCompleto(pedidoOuCampo)
                    || this.campos[pedidoOuCampo.campo]?.label
                    || pedidoOuCampo.campo
            }
            return servicePCM.labelCampoPedido(pedidoOuCampo) || this.campos[pedidoOuCampo]?.label || pedidoOuCampo
        },
        metaCampo(campo) {
            return servicePCM.metaCampoPedido(campo) || this.campos[campo] || null
        },
        usaTextarea(pedido) {
            const parsed = servicePCM.parseCampoPedidoResultado(pedido?.campo)
            if (!parsed) return pedido?.campo === 'objetivos_estrategicos'
            if (parsed.tipo === 'lista' || parsed.tipo === 'novo' || parsed.tipo === 'excluir') return true
            if (parsed.tipo === 'campo' && (parsed.atributo === 'descricao' || parsed.atributo === 'evidencias')) {
                return true
            }
            return false
        },
        textoParaResumo(valor) {
            const t = (valor ?? '').toString()
            return t.trim() === '' ? '(vazio)' : t
        },
        normalizarTexto(valor) {
            return (valor ?? '').toString().trim()
        },
        normalizarCusto(valor) {
            if (valor === '' || valor == null) return null
            const n = Number(valor)
            return Number.isFinite(n) ? n : null
        },
        custoParaResumo(valor) {
            const n = this.normalizarCusto(valor)
            if (n == null) return '(vazio)'
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n)
        },
        formatarMoeda(valor) {
            return this.custoParaResumo(valor)
        },
        formatarValorExibicao(pedido, qual) {
            const parsed = servicePCM.parseCampoPedidoResultado(pedido?.campo)
            if (parsed?.tipo === 'lista' || pedido?.campo === 'resultados') {
                return qual === 'atual'
                    ? this.resumoPedidoResultados(pedido).valorAntigo
                    : this.resumoPedidoResultados(pedido).novoValor
            }
            if (parsed && parsed.tipo !== 'lista') {
                return servicePCM.formatarValorPedidoResultado(pedido, qual)
            }
            if (pedido?.campo === 'objetivos_estrategicos') {
                return qual === 'atual'
                    ? this.resumoPedidoObjetivos(pedido).valorAntigo
                    : this.resumoPedidoObjetivos(pedido).novoValor
            }
            const valor = qual === 'atual' ? pedido.valor_atual : pedido.novo_valor
            const meta = this.metaCampo(pedido.campo)
            if (meta?.tipo === 'date' && valor) {
                return formatarDataBr(String(valor).slice(0, 10))
            }
            if (meta?.tipo === 'boolean') {
                return servicePCM.formatarBoolSemCusto(valor)
            }
            return this.textoParaResumo(valor)
        },
        parseResultadosPedido(valor) {
            if (!valor) return []
            try {
                const parsed = JSON.parse(valor)
                return Array.isArray(parsed) ? parsed : []
            } catch {
                return []
            }
        },
        parseObjetivosPedido(valor) {
            if (!valor) return []
            try {
                const parsed = JSON.parse(valor)
                return Array.isArray(parsed) ? parsed : []
            } catch {
                return []
            }
        },
        resumoItemCompleto(item, rotulo) {
            return servicePCM.resumoItemResultadoCompleto(item, rotulo)
        },
        resumoPedidoObjetivos(pedido) {
            const anterior = this.parseObjetivosPedido(pedido?.valor_atual)
            const proposto = this.parseObjetivosPedido(pedido?.novo_valor)
            const formatarLista = (lista) => {
                if (!lista.length) return '(nenhum objetivo selecionado)'
                return lista.map((item) => `- ${item.nome || `Objetivo #${item.objetivo_id ?? '?'}`}`).join('\n')
            }
            return {
                valorAntigo: formatarLista(anterior),
                novoValor: formatarLista(proposto),
            }
        },
        resumoPedidoResultados(pedido) {
            const anterior = this.parseResultadosPedido(pedido?.valor_atual)
            const proposto = this.parseResultadosPedido(pedido?.novo_valor)

            const porIdAnterior = new Map(
                anterior.filter((i) => i && i.id != null).map((i) => [String(i.id), i]),
            )
            const porIdProposto = new Map(
                proposto.filter((i) => i && i.id != null).map((i) => [String(i.id), i]),
            )

            const blocosAntigo = [`Total de itens antes: ${anterior.length}`]
            const blocosNovo = [`Total de itens na proposta: ${proposto.length}`]

            proposto.forEach((itemNovo, idx) => {
                const indice = idx + 1
                if (itemNovo?.id == null) {
                    blocosAntigo.push(`—\nItem ${indice} (novo): não existia na lista anterior.`)
                    blocosNovo.push(`—\n${this.resumoItemCompleto(itemNovo, `Item ${indice} (novo)`)}`)
                    return
                }

                const itemAntigo = porIdAnterior.get(String(itemNovo.id))
                if (!itemAntigo) {
                    blocosAntigo.push(`—\nItem ${indice}: não existia na lista anterior (id ${itemNovo.id}).`)
                    blocosNovo.push(`—\n${this.resumoItemCompleto(itemNovo, `Item ${indice}`)}`)
                    return
                }

                const partesAntigo = []
                const partesNovo = []
                let houveAlteracao = false

                if (this.normalizarTexto(itemAntigo.descricao) !== this.normalizarTexto(itemNovo.descricao)) {
                    houveAlteracao = true
                    partesAntigo.push(`Descrição (antes):\n${this.textoParaResumo(itemAntigo.descricao)}`)
                    partesNovo.push(`Descrição (depois):\n${this.textoParaResumo(itemNovo.descricao)}`)
                }
                if (!!itemAntigo.sem_custo !== !!itemNovo.sem_custo
                    || this.normalizarCusto(itemAntigo.custo) !== this.normalizarCusto(itemNovo.custo)) {
                    houveAlteracao = true
                    partesAntigo.push(itemAntigo.sem_custo
                        ? 'Custo (antes): esta entrega não terá custo'
                        : `Custo (antes): ${this.custoParaResumo(itemAntigo.custo)}`)
                    partesNovo.push(itemNovo.sem_custo
                        ? 'Custo (depois): esta entrega não terá custo'
                        : `Custo (depois): ${this.custoParaResumo(itemNovo.custo)}`)
                }

                if (houveAlteracao) {
                    blocosAntigo.push(`—\nItem ${indice}:\n${partesAntigo.join('\n\n')}`)
                    blocosNovo.push(`—\nItem ${indice}:\n${partesNovo.join('\n\n')}`)
                }
            })

            anterior.forEach((itemAntigo, idx) => {
                if (itemAntigo?.id == null || porIdProposto.has(String(itemAntigo.id))) return
                const indice = idx + 1
                blocosAntigo.push(`—\n${this.resumoItemCompleto(itemAntigo, `Item ${indice} (será removido)`)}`)
                blocosNovo.push(`—\nItem ${indice}: removido na proposta.`)
            })

            return {
                valorAntigo: blocosAntigo.join('\n') || 'Sem alterações detectadas',
                novoValor: blocosNovo.join('\n') || 'Sem alterações detectadas',
            }
        },
    },
}
</script>

<style scoped>
.pedido-mudanca-item {
    margin-block: 1rem;
    gap: 1rem;
}

.pedido-mudanca-ordem {
    margin: 0;
}

.select-readonly {
    opacity: 1;
    cursor: auto;
}

.pedido-mudanca-status {
    align-self: end;
    justify-self: end;
    text-align: right;
}

@media screen and (min-width: 971px) {
    .pedido-mudanca-status {
        grid-column: 4;
    }
}
</style>
